import React, { createContext, useEffect, useMemo, useState } from "react";
import { IBreadcrumbItem, IBreadcrumbProps } from "@fluentui/react";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { Divider, Paper, Stack } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProductFilterPill } from "../components/ProductView/Common/ProductFilterPill";
import { PRODUCT_VIEW_DATA } from "../components/ProductView/Common/ProductView.constants";
import { ProductViewFiltersProps } from "../components/ProductView/Common/ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "../components/ProductView/Common/ProductView.styles";
import ProductView from "../components/ProductView/ProductView";
import { logException, logPageView } from "../helper/AppInsightsHelper";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import {
  useGetFavoriteProductsByLoginQuery,
  useInsertOrUpdateFavoriteProductsMutation,
  useInsertRecentlyViewedProductMutation
} from "../slices/ExploreProducts.slice";
import { isValidJson } from "../Utils/js-common";

export const ProductViewFiltersContext = createContext<ProductViewFiltersProps>(
  PRODUCT_VIEW_DATA.PRODUCT_VIEW.INITIAL_PRODUCT_VIEW_FILTERS
);

export default function ProductPage() {
  const navigate = useNavigate();
  const [product] = useSearchParams();
  const [productName, setProductName] = useState(
    product?.get("productname") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_NAME
  );
  const productId: number = parseInt(product?.get("productid") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_ID);
  const productFamily: string = product?.get("productfamily") ?? "";
  const isPartnerInfluenceTab = product?.get("tab") === "Partner Influence";
  const userName = sessionStorage.getItem("loginName")?.toString();
  const userEmail = sessionStorage.getItem("loginUserName")?.toString();
  const [regionValue, setRegionValue] = useState("");
  const [segmentGroupValue, setSegmentGroupValue] = useState("");
  const [segmentValue, setSegmentValue] = useState("");
  const [subSegmentValue, setSubSegmentValue] = useState("");
  const [industryValue, setIndustryValue] = useState("");
  const [hasPartners, setHasPartner] = useState("");
  const [isFastTracks, setIsFastTrack] = useState("");
  const [isCats, setIsCat] = useState("");
  const [isSsts, setIsSst] = useState("");
  const [isS500, setIsS500] = useState("");
  const [isCCP, setIsCCP] = useState("");
  const [isTCT, setIsTCT] = useState("");
  const [majorGrowth, setMajorGrowth] = useState("");
  const [insertRecentlyViewedProduct] = useInsertRecentlyViewedProductMutation();
  const [isFavorited, setIsFavorited] = useState(false);
  const [insertOrUpdateFavoriteProduct] = useInsertOrUpdateFavoriteProductsMutation();
  const { data: favoriteProductsData, error: favoriteProductsError, refetch } = useGetFavoriteProductsByLoginQuery({});

  const breadcrumbProps: IBreadcrumbProps = {
    items: [
      { text: "Home", key: "explore-products-to-home", onClick: () => navigate("/") },
      { text: "Explore Products", key: "explore-products-list", onClick: () => navigate("/products") },
      {
        text: productName,
        key: "explore-products-with-product-name",
        isCurrentItem: true
      }
    ] as IBreadcrumbItem[],
    styles: PRODUCT_VIEW_STYLES.PRODUCT_PAGE.CSS_STYLES.productViewCustomBreadcrumb,
    onRenderItem: (item, defaultRender) => {
      if (item && item.key === "explore-products-with-product-name") {
        const textContent = defaultRender ? defaultRender(item) : <>{item?.text}</>;
        return (
          <Stack direction="row" alignItems="center">
            {textContent}
            <LoadFavoriteIcon
              favIcon={isFavorited}
              updateFavIcon={setIsFavorited}
              onFavoriteChange={handleFavoriteChange}
            />
          </Stack>
        );
      }
      return defaultRender ? defaultRender(item) : <>{item?.text}</>;
    }
  };

  const handleProviderValue = useMemo(
    () => ({
      hasPartner: hasPartners,
      industry: industryValue,
      isCAT: isCats,
      isFastTrackStrategic: isFastTracks,
      isSST: isSsts,
      productName: productName,
      productId: productId,
      productFamily: productFamily,
      region: regionValue,
      segment: segmentValue,
      subSegment: subSegmentValue,
      segmentGroup: segmentGroupValue,
      isS500: isS500,
      isCCP: isCCP,
      isTCT: isTCT,
      majorGrowth: majorGrowth
    }),
    [
      hasPartners,
      industryValue,
      isCats,
      isFastTracks,
      isSsts,
      productName,
      productFamily,
      regionValue,
      segmentGroupValue,
      segmentValue,
      subSegmentValue,
      isS500,
      isCCP,
      isTCT,
      majorGrowth
    ]
  );

  const commonColor: string = "hsl(206, 100%, 42%)";
  const LoadFavoriteIcon = (props: {
    favIcon: boolean;
    updateFavIcon: (status: boolean) => void;
    onFavoriteChange: (isFavorite: boolean) => void;
  }) => {
    return (
      <Stack style={{ display: "inline-flex" }}>
        {props.favIcon ? (
          <FavoriteIcon
            titleAccess="Click to unfavorite this product"
            sx={{
              color: commonColor,
              fontSize: "1.4rem"
            }}
            style={{ cursor: "pointer" }}
            onClick={() => {
              props.updateFavIcon(false);
              props.onFavoriteChange(false);
            }}
          />
        ) : (
          <FavoriteBorderIcon
            titleAccess="Click to favorite this product"
            sx={{
              color: commonColor,
              fontSize: "1.4rem"
            }}
            style={{ cursor: "pointer" }}
            onClick={() => {
              props.updateFavIcon(true);
              props.onFavoriteChange(true);
            }}
          />
        )}
      </Stack>
    );
  };

  const handleFavoriteChange = (isFavorite: boolean) => {
    insertOrUpdateFavoriteProduct({
      productId: productId,
      latestProductName: productName,
      loginName: userName ?? "",
      loginEmail: userEmail ?? "",
      isFavorite: Number(isFavorite)
    }).then(() => {
      refetch();
    });
  };

  useEffect(() => {
    refetch();
  }, [location, productName]);

  useEffect(() => {
    if (favoriteProductsData && Array.isArray(favoriteProductsData)) {
      const productData = favoriteProductsData.find((product) => product.productId === productId);
      if (productData) {
        setIsFavorited(productData.isFavorite);
      } else {
        setIsFavorited(false);
      }
    } else if (favoriteProductsError) {
      logException("Product View Page", "ProductView", "getFavoriteProductsByLogin", favoriteProductsError);
    }
  }, [favoriteProductsData, favoriteProductsError, productId]);

  useEffect(() => {
    const customPropsForLogging = {
      productName: productName
    };

    logPageView("Product View Page", window.location.href, customPropsForLogging);
  }, []);

  // This code stores recently viewed product names in session storage, maintaining a list upto 15 unique entries.
  // New products are added to the top, duplicates are avoided, and each new entry is also sent to the API.
  useEffect(() => {
    if (productId?.toString()) {
      const viewedKey = PRODUCT_VIEW_DATA.PRODUCT_VIEW.VIEWED_KEY;
      const checkExisting = sessionStorage.getItem(viewedKey);
      const MAX_RECENTLY_VIEWED = PRODUCT_VIEW_DATA.PRODUCT_VIEW.RECENTLY_VIEWED_LIMIT;
      let viewedList: string[] = [];
      try {
        viewedList = checkExisting && isValidJson(checkExisting) ? JSON.parse(checkExisting) : [];
      } catch (e) {
        console.error(
          "Failed to parse recently viewed products from sessionStorage.Expected a JSON array of strings.",
          e
        );
      }
      if (productId && !viewedList?.includes(productName)) {
        viewedList.unshift(productName);
        if (viewedList.length > MAX_RECENTLY_VIEWED) {
          viewedList.pop();
        }
        try {
          sessionStorage.setItem(viewedKey, JSON.stringify(viewedList));
        } catch (e) {
          console.error("Failed to save recently viewed products to sessionStorage", e);
        }
        insertRecentlyViewedProduct({
          LoginName: userName ?? "",
          LoginEmail: userEmail ?? "",
          ProductId: productId?.toString(),
          ProductName: productName
        });
      }
    }
  }, [productId]);

  return (
    <div className={PRODUCT_VIEW_STYLES.PRODUCT_PAGE.CLASS_NAMES.productPageContainer}>
      <ProductViewFiltersContext.Provider value={handleProviderValue}>
        <Paper>
          <CustomBreadcrumb {...breadcrumbProps} />
          <Divider />
          <ProductFilterPill
            productName={productName}
            hideFilters={isPartnerInfluenceTab}
            onProductChange={setProductName}
            onRegionChange={setRegionValue}
            onSegmentChange={setSegmentValue}
            onSubSegmentChange={setSubSegmentValue}
            onIndustryChange={setIndustryValue}
            onPartnerChange={setHasPartner}
            onFastTrackChange={setIsFastTrack}
            onIsCatChange={setIsCat}
            onIsSstChange={setIsSst}
            onIsS500Change={setIsS500}
            onSegmentGroupChange={setSegmentGroupValue}
            onIsCCPChange={setIsCCP}
            onIsTCTChange={setIsTCT}
            onMajorGrowthChange={setMajorGrowth}
          />
        </Paper>
        <ProductView />
      </ProductViewFiltersContext.Provider>
    </div>
  );
}
